// =============================================================================
// MeuDriver — Hook: useProgressReport (Batch T4)
// Agrega dados de múltiplos hooks para gerar estrutura do relatório imprimível
// do estudante.
// =============================================================================

import { useMemo } from 'react'
import { useStudentProgressSummary, useStudentLessonHistory } from '@/hooks/useStudentProgress'
import { useStudentBadges } from '@/hooks/useStudentBadges'
import { useStudentMilestoneProgress, useLearningMilestones } from '@/hooks/useLearningPath'
import { useMyCertificates } from '@/hooks/useCertificates'
import { useAuth } from '@/hooks/useAuth'

// ─── Tipos do relatório ───────────────────────────────────────────────────────

export interface ReportLesson {
  id: string
  scheduled_at: string
  instructor_name: string | null
  duration_minutes: number
  status: string
}

export interface ReportMilestone {
  id: string
  title: string
  badge_icon: string
  achieved_at: string | null
  achieved: boolean
  order_index: number
}

export interface ReportBadge {
  id: string
  name: string
  icon_emoji: string
  earned: boolean
  earned_at: string | null
}

export interface ReportCertificate {
  id: string
  certificate_number: string
  total_lessons: number
  total_hours: number
  issued_at: string
  instructor_name: string | null
}

export interface ProgressReportData {
  studentName: string | null
  generatedAt: Date
  // KPIs
  totalLessons: number
  totalHours: number
  // Conteúdo
  milestones: ReportMilestone[]
  badges: ReportBadge[]
  certificates: ReportCertificate[]
  recentLessons: ReportLesson[]
  // Estado de carregamento agregado
  isLoading: boolean
}

// ─── Hook principal ───────────────────────────────────────────────────────────

export function useProgressReport(): ProgressReportData {
  const { profile } = useAuth()

  const { data: summary, isLoading: l1 } = useStudentProgressSummary()
  const { data: lessonHistory = [], isLoading: l2 } = useStudentLessonHistory()
  const { data: badges = [], isLoading: l3 } = useStudentBadges()
  const { data: milestones = [], isLoading: l4 } = useLearningMilestones()
  const { data: milestoneProgress = [], isLoading: l5 } = useStudentMilestoneProgress()
  const { data: certificates = [], isLoading: l6 } = useMyCertificates()

  const isLoading = l1 || l2 || l3 || l4 || l5 || l6

  // Set de milestone IDs alcançados
  const achievedMilestoneMap = useMemo(() => {
    const map = new Map<string, string>()
    for (const mp of milestoneProgress) {
      map.set(mp.milestone_id, mp.achieved_at)
    }
    return map
  }, [milestoneProgress])

  // Milestones com status de conquista
  const reportMilestones: ReportMilestone[] = useMemo(
    () =>
      milestones.map((m) => ({
        id:         m.id,
        title:      m.title,
        badge_icon: m.badge_icon,
        achieved:   achievedMilestoneMap.has(m.id),
        achieved_at: achievedMilestoneMap.get(m.id) ?? null,
        order_index: m.order_index,
      })),
    [milestones, achievedMilestoneMap],
  )

  // Badges conquistados (apenas os earned para relatório)
  const reportBadges: ReportBadge[] = useMemo(
    () =>
      badges
        .filter((b) => b.earned)
        .map((b) => ({
          id:         b.id,
          name:       b.name,
          icon_emoji: b.icon_emoji,
          earned:     b.earned,
          earned_at:  b.earned_at,
        })),
    [badges],
  )

  // Certificados
  const reportCertificates: ReportCertificate[] = useMemo(
    () =>
      certificates.map((c) => {
        const instructor = c.instructor as {
          profile?: { full_name?: string | null } | null
        } | null
        return {
          id:                 c.id,
          certificate_number: c.certificate_number,
          total_lessons:      c.total_lessons,
          total_hours:        c.total_hours,
          issued_at:          c.issued_at,
          instructor_name:    instructor?.profile?.full_name ?? null,
        }
      }),
    [certificates],
  )

  // Últimas 10 aulas concluídas
  const recentLessons: ReportLesson[] = useMemo(
    () =>
      lessonHistory.slice(0, 10).map((l) => ({
        id:               l.id,
        scheduled_at:     l.scheduled_at,
        instructor_name:  l.instructor_name,
        duration_minutes: l.duration_minutes ?? 60,
        status:           l.status,
      })),
    [lessonHistory],
  )

  return {
    studentName:     profile?.full_name ?? null,
    generatedAt:     new Date(),
    totalLessons:    summary?.totalLessons ?? 0,
    totalHours:      summary?.totalHours ?? 0,
    milestones:      reportMilestones,
    badges:          reportBadges,
    certificates:    reportCertificates,
    recentLessons,
    isLoading,
  }
}
